import React, { useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';
import { calculateAge } from '@this/domain/age_util';
import { Text } from '@this/components/shared/ui/data_displays/typography';
import { isWithinSixMonth, isOverDate } from '@this/src/components/shared/checker/date_checker';
import moment from 'moment';
import type { Moment } from '@this/src/lib/moment';
import type TravelerInformation from '@this/src/domain/traveler_information';
import { observer } from 'mobx-react';
import { FormField, FormTitle, InputGroup, TextInput } from './order_item_traveler.style';

type Props = {
  travelerInformation: TravelerInformation;
  transportDate: Moment | null;
  hiddenPersonalForm?: boolean;
  classNameForModifiedField: (...path: (string | number)[]) => string;
};

const OrderItemTravelerForeignAirFormComponent: React.FC<Props> = ({
  travelerInformation,
  transportDate,
  hiddenPersonalForm = false,
  classNameForModifiedField
}) => {
  const [birthdayInput, setBirthdayInput] = useState(
    travelerInformation.flightBirthday?.format('YYYY-MM-DD') ?? ''
  );

  useEffect(() => {
    const formatted = travelerInformation.flightBirthday?.format('YYYY-MM-DD') ?? '';
    setBirthdayInput(formatted);
  }, [travelerInformation.flightBirthday]);

  const handleBirthdayChange = (value: string) => {
    setBirthdayInput(value);
  };

  const handleBirthdayBlur = () => {
    if (birthdayInput === '') {
      travelerInformation.flightBirthday = undefined;
      return;
    }

    const parsed = moment(birthdayInput, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      travelerInformation.flightBirthday = parsed;
      setBirthdayInput(parsed.format('YYYY-MM-DD'));
      return;
    }

    travelerInformation.flightBirthday = undefined;
  };

  return (
    <Container>
      <FormTitle>航空券情報</FormTitle>
      {!hiddenPersonalForm && (
        <>
          <FormField>
            <label
              className={`
                ${classNameForModifiedField('flightLastNameKana')}
                ${classNameForModifiedField('flightFirstNameKana')}
              `}
            >
              氏名(カタカナ)
            </label>
            <InputGroup>
              <TextInput
                type="text"
                placeholder="姓(カタカナ)"
                value={travelerInformation.flightLastNameKana ?? ''}
                onChange={e => {
                  travelerInformation.flightLastNameKana = e.target.value;
                }}
              />
              <TextInput
                type="text"
                placeholder="名(カタカナ)"
                value={travelerInformation.flightFirstNameKana ?? ''}
                onChange={e => {
                  travelerInformation.flightFirstNameKana = e.target.value;
                }}
              />
            </InputGroup>
          </FormField>
          <FormField>
            <label
              className={`
                ${classNameForModifiedField('flightLastName')}
                ${classNameForModifiedField('flightFirstName')}
              `}
            >
              氏名(ローマ字)
            </label>
            <InputGroup>
              <TextInput
                type="text"
                placeholder="姓(ローマ字)"
                value={travelerInformation.flightLastName ?? ''}
                onChange={e => {
                  travelerInformation.flightLastName = e.target.value;
                }}
              />
              <TextInput
                type="text"
                placeholder="ミドルネーム(ローマ字)"
                value={travelerInformation.flightMiddleName ?? ''}
                onChange={e => {
                  travelerInformation.flightMiddleName = e.target.value;
                }}
              />
              <TextInput
                type="text"
                placeholder="名(ローマ字)"
                value={travelerInformation.flightFirstName ?? ''}
                onChange={e => {
                  travelerInformation.flightFirstName = e.target.value;
                }}
              />
            </InputGroup>
          </FormField>
          <FormField>
            <label className={classNameForModifiedField('flightBirthday')}>誕生日</label>
            <TextInput
              placeholder="例) 2020-04-01"
              value={birthdayInput}
              onChange={e => handleBirthdayChange(e.target.value)}
              onBlur={handleBirthdayBlur}
            />
            {travelerInformation.flightBirthday && transportDate && (
              <FlightAge>
                <label>搭乗時年齢</label>
                <span>{`${calculateAge(travelerInformation.flightBirthday, transportDate).toString()}歳`}</span>
              </FlightAge>
            )}
          </FormField>
          <FormField>
            <label className={classNameForModifiedField('flightGender')}>性別</label>
            <InputGroup>
              <label>
                <input
                  type="radio"
                  value="male"
                  name="foreign-air-gender"
                  checked={travelerInformation.flightGender === 'male'}
                  onChange={e => {
                    travelerInformation.flightGender = 'male';
                  }}
                />
                男性
              </label>
              <label>
                <input
                  type="radio"
                  value="female"
                  name="foreign-air-gender"
                  checked={travelerInformation.flightGender === 'female'}
                  onChange={e => {
                    travelerInformation.flightGender = 'female';
                  }}
                />
                女性
              </label>
            </InputGroup>
          </FormField>
        </>
      )}

      <FormField>
        <label className={classNameForModifiedField('flightTel')}>緊急連絡先番号</label>
        <TextInput
          placeholder="例) 09012345678"
          value={travelerInformation.flightTel ?? ''}
          onChange={e => {
            travelerInformation.flightTel = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('passportNumber')}>パスポート番号</label>
        <TextInput
          placeholder="例) AA1234567"
          value={travelerInformation.passportNumber ?? ''}
          onChange={e => {
            travelerInformation.passportNumber = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('passportExpire')}>パスポート有効期限</label>
        <TextInput
          placeholder="例) 2025-01-01"
          value={travelerInformation.passportExpire ?? ''}
          onChange={e => {
            travelerInformation.passportExpire = e.target.value;
          }}
        />
        {isWithinSixMonth(travelerInformation.passportExpire) && (
          <div>
            <Text color="danger">有効期限が近づいてきています。パスポート情報をご確認ください。</Text>
          </div>
        )}
        {isOverDate(travelerInformation.passportExpire) && (
          <div>
            <Text color="danger">有効期限が切れています。パスポート情報をご確認ください。</Text>
          </div>
        )}
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('passportIssuedCountry')}>パスポート発行国</label>
        <TextInput
          placeholder="例) 日本"
          value={travelerInformation.passportIssuedCountry ?? ''}
          onChange={e => {
            travelerInformation.passportIssuedCountry = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('airSeat')}>座席の希望</label>
        <TextInput
          placeholder="例) 前方通路側"
          value={travelerInformation.airSeat ?? ''}
          onChange={e => {
            travelerInformation.airSeat = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('mileageNumber')}>マイル番号</label>
        <TextInput
          placeholder="例) 0123456789"
          value={travelerInformation.mileageNumber ?? ''}
          onChange={e => {
            travelerInformation.mileageNumber = e.target.value;
          }}
        />
      </FormField>
    </Container>
  );
};

const Container = styled.div``;

const FlightAge = styled.div`
  margin-bottom: 0.75em;
`;

const OrderItemTravelerForeignAirForm = observer(OrderItemTravelerForeignAirFormComponent);
export default OrderItemTravelerForeignAirForm;
